// Typed fetch wrappers for every backend endpoint.
// All requests inject the auth token from localStorage and handle global errors.
//
// Cloudflare Pages port: in production, the SPA and the /api Functions are served
// from the same origin, so a relative "/api" base works. In dev you can either
// run `wrangler pages dev` (which wires the same-origin /api) and leave VITE_API_BASE
// unset, or run vite alone and point at a remote backend via VITE_API_BASE.

const TOKEN_KEY = "nedm.staff.token";

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE?.toString().replace(/\/$/, "") || "";

export function getToken(): string | null {
  return typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
}

export function setToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, message: string, body: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOptions {
  method?: Method;
  body?: any;
  query?: Record<string, string | number | boolean | undefined>;
  // For routes that don't require staff auth (intake page).
  publicRoute?: boolean;
  // For file uploads.
  formData?: FormData;
  // Override Authorization header (e.g., intake token).
  authOverride?: string;
}

function buildUrl(path: string): URL {
  // If an absolute API_BASE was provided (e.g. https://api.example.com), resolve
  // relative paths against it; otherwise resolve against window origin (same-origin).
  if (API_BASE && /^https?:\/\//i.test(API_BASE)) {
    // path is expected to start with "/api/..."; strip leading slash and join.
    return new URL(path.replace(/^\//, ""), API_BASE.endsWith("/") ? API_BASE : API_BASE + "/");
  }
  return new URL(path, window.location.origin);
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, publicRoute, formData, authOverride } = opts;

  const url = buildUrl(path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {};
  if (!formData) headers["Content-Type"] = "application/json";

  if (authOverride) {
    headers["Authorization"] = `Bearer ${authOverride}`;
  } else if (!publicRoute) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method,
      headers,
      body: formData ? formData : body ? JSON.stringify(body) : undefined,
      credentials: "include",
    });
  } catch (e: any) {
    throw new ApiError(0, e?.message || "Network error", null);
  }

  let parsed: any = null;
  const text = await res.text();
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }

  if (!res.ok) {
    // Global 402 handling: the company's access is paused. Bounce to the
    // /suspended screen (unless we're already there) so staff see the paywall
    // notice instead of a broken page. Mirrors the 401 redirect below.
    if (
      res.status === 402 &&
      parsed && parsed.error === "company_suspended" &&
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/suspended")
    ) {
      window.location.href = "/suspended";
    }
    // Global 401 handling: drop token, force re-login.
    // EXCEPT on public client-facing pages (/intake/<token>, /filing/<token>) —
    // those are token-authenticated and the AuthProvider's boot-time /me check
    // is expected to 401 for anonymous clients. Don't bounce them to staff
    // sign-in; let the page render its own public form.
    if (res.status === 401 && !publicRoute) {
      setToken(null);
      if (
        typeof window !== "undefined" &&
        // "/" is the public marketing landing page (with /privacy + /terms) —
        // AuthProvider's boot-time /me probe 401s for every anonymous visitor
        // there, and bouncing them to staff sign-in hides the site (this bug
        // made affiliate reviewers see a login wall instead of the landing).
        window.location.pathname !== "/" &&
        !window.location.pathname.startsWith("/privacy") &&
        !window.location.pathname.startsWith("/terms") &&
        !window.location.pathname.startsWith("/disclosure") &&
        !window.location.pathname.startsWith("/details/") &&
        !window.location.pathname.startsWith("/with/") &&
        !window.location.pathname.startsWith("/blog") &&
        !window.location.pathname.startsWith("/contact") &&
        !window.location.pathname.startsWith("/pricing") &&
        !window.location.pathname.startsWith("/welcome") &&
        !window.location.pathname.startsWith("/affiliate/") &&
        !window.location.pathname.startsWith("/login") &&
        // Staff password recovery must render for anonymous visitors — bouncing
        // them to /login makes the reset flow impossible to complete.
        !window.location.pathname.startsWith("/forgot-password") &&
        !window.location.pathname.startsWith("/reset-password") &&
        !window.location.pathname.startsWith("/intake/") &&
        !window.location.pathname.startsWith("/start/") &&
        !window.location.pathname.startsWith("/start/") &&
        !window.location.pathname.startsWith("/apply/") &&
        !window.location.pathname.startsWith("/filing/") &&
        !window.location.pathname.startsWith("/portal/") &&
        !window.location.pathname.startsWith("/join/") &&
        !window.location.pathname.startsWith("/staff-invite/") &&
        !window.location.pathname.startsWith("/voe/") &&
        !window.location.pathname.startsWith("/upload-docs/") &&
        !window.location.pathname.startsWith("/suspended")
      ) {
        window.location.href = "/login";
      }
    }
    // Prefer the human-readable message over the raw error code so forms show
    // "Password must be at least 8 characters" instead of "validation_error".
    const msg = (parsed && (parsed.message || parsed.error)) || res.statusText;
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

/* ---------- domain types ---------- */

export type ClientStatus = "pending" | "onboarding" | "active" | "suspended" | "needs_review" | "paused" | "completed" | "churned";
export type MonitoringProvider =
  | "my_free_score_now" | "smart_credit" | "identity_iq" | "my_score_iq" | "experian" | "other" | "not_started";

/** Display names + which score model each reports — MyScoreIQ is the only FICO one. */
export const MONITORING_PROVIDERS: Array<{ value: Exclude<MonitoringProvider, "not_started">; label: string; note: string }> = [
  { value: "my_free_score_now", label: "MyFreeScoreNow", note: "VantageScore 3.0" },
  { value: "smart_credit", label: "SmartCredit", note: "VantageScore" },
  { value: "identity_iq", label: "IdentityIQ", note: "VantageScore 3.0" },
  { value: "my_score_iq", label: "MyScoreIQ", note: "real FICO scores — what lenders use" },
  { value: "experian", label: "Experian", note: "no auto-pull — capture from a signed-in browser or upload the PDF" },
  { value: "other", label: "Other / manual", note: "store a login for a provider we don't auto-pull; capture via browser or PDF" },
];

export interface Client {
  id: string;
  created_at: string;
  status: ClientStatus;
  legal_name: string;
  preferred_name?: string | null;
  email: string;
  phone: string;
  dob_masked?: string | null; // "XX/XX/1985"
  ssn_masked?: string | null; // "XXX-XX-1234"
  address_line1?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  assigned_agent?: string | null;
  monthly_price?: number | null;
  monitoring: MonitoringProvider;
  /** Monitoring login (MFSN/SmartCredit/IdentityIQ/Experian/…) on file. The
   *  username shows as-is; the password only comes down via the audited
   *  reveal endpoint. */
  monitoring_username?: string | null;
  has_monitoring_credentials?: boolean;
  score_start?: number | null;
  score_current?: number | null;
  negative_items?: number;
  deleted_items?: number;
  next_action?: string | null;
  notes?: string | null;
  service_agreement_signed: boolean;
  /** Credit Karma login on file (for the operator's manual CK capture). The
   *  password never comes down — only these three fields + the audited reveal. */
  has_credit_karma_credentials?: boolean;
  credit_karma_login_email?: string | null;
  credit_karma_credentials_updated_at?: string | null;
  /** Latest service-agreement record summary (see /api/clients/:id GET). */
  service_agreement?: ServiceAgreementSummary;
  /** Documents uploaded during intake, with staff-authed view_url (v1.1). */
  identity_documents?: Array<{
    id: string;
    kind: string;
    file_url: string;
    uploaded_at: string;
    verified_at: string | null;
    view_url: string | null;
  }>;
  /** FCRA written authorization on file — the permissible purpose for a credit pull. */
  credit_authorization?: {
    signed_at: string; signed_name: string; version: string; method: string;
  } | null;
}

export type ServiceAgreementSummary =
  | { state: "not_sent" }
  | {
      state: "pending" | "signed" | "expired" | "revoked";
      id: string;
      template_version: string | null;
      sent_at: string | null;
      sent_to_email: string | null;
      signed_at: string | null;
      signed_by_name: string | null;
      sign_token_expires_at: string | null;
      revoked_at: string | null;
    };

export interface ClientListItem extends Client {}

export interface IntakeInvitation {
  id: string;
  client_id: string;
  token: string;
  delivery_channel: "sms" | "email" | "manual_link";
  delivered_to: string;
  expires_at: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  invite_url?: string;
}

export interface CreditReport {
  id: string;
  client_id: string;
  provider: "my_free_score_now" | "smart_credit" | "manual_upload" | "credit_karma" | "isoftpull" | "identity_iq" | "my_score_iq" | "pdf_upload";
  file_url: string;
  pulled_at: string;
  imported_at: string;
  parse_status: "pending" | "parsed" | "failed" | "needs_manual";
  parse_error?: string | null;
  /** Joined from clients so the list is readable; null if the client is gone. */
  client_name?: string | null;
}

export interface DisputeRound {
  id: string;
  client_id: string;
  client_name?: string;
  round_number: number;
  // Backend canonical states.
  status: "drafting" | "ready" | "mailed" | "response_due" | "partially_complete" | "complete";
  bureau: "equifax" | "experian" | "transunion" | "all";
  items_count: number;
  due_date?: string | null;
  response_deadline?: string | null;
  notes?: string | null;
  mailed_at?: string | null;
  created_at: string;
}

export interface Letter {
  id: string;
  client_id: string;
  client_name?: string;
  dispute_round_id?: string | null;
  round_id?: string | null;
  kind: string;
  bureau?: string;
  recipient_bureau?: string;
  recipient?: string;
  // Backend uses these status values; older copy might still use draft/ready.
  // We keep the union broad so the StatusPill / table can render either.
  status:
    | "draft"
    | "drafting"
    | "quality_gate_pending"
    | "approved"
    | "ready"
    | "queued"
    | "queued_for_mail"
    | "mailed"
    | "delivered"
    | "responded";
  pdf_url?: string | null;
  body_text?: string | null;
  recipient_address?: string | null;
  // JSON-encoded analyzer + section metadata. For analyzer-generated letters this
  // carries { analysis_finding_ids, credit_report_id, source, fcra_basis,
  // manual_section?, manual_section_label? } so the UI can label each letter
  // (e.g. "§611 Hard Inquiries") without a new letter_kind enum value.
  quality_gate_notes?: string | null;
  created_at: string;
}

export type MailProviderId = "lob" | "letterstream" | "manual" | "other";
export type MailJobStatus =
  | "draft"
  | "queued"
  | "sent"
  | "in_transit"
  | "delivered"
  | "returned"
  | "failed"
  | "cancelled"
  | "staff_mailed";

export interface MailProviderInfo {
  id: MailProviderId;
  name: string;
  supported_mail_types: ReadonlyArray<"first_class" | "certified">;
  configured: boolean;
  inflight: number;
}

export interface MailJob {
  id: string;
  letter_id: string;
  client_id?: string;
  client_name?: string;
  type?: string;
  letter_kind?: string | null;
  recipient_bureau?: string | null;
  // Backend doesn't include `destination` — it's a derived legacy field.
  destination?: string;
  provider: MailProviderId;
  provider_job_id?: string | null;
  // Backend canonical field. `delivery_class` is the legacy frontend alias.
  service_type?: "first_class" | "certified" | "certified_rr";
  delivery_class?: "first_class" | "certified" | "certified_rr";
  status: MailJobStatus;
  tracking_number?: string | null;
  return_receipt_url?: string | null;
  raw_provider_payload?: Record<string, unknown> | null;
  cost_cents?: number | null;
  sent_at?: string | null;
  delivered_at?: string | null;
  created_at: string;
}

export interface Filing {
  id: string;
  client_id: string;
  client_name?: string;
  agency: "ftc_identitytheft" | "cfpb_complaint" | "bureau_direct";
  submitted_by: "client" | "operator";
  attestation_text?: string;
  client_ip?: string;
  client_user_agent?: string;
  submitted_at?: string;
  status: "pre_filled" | "awaiting_client" | "submitted" | "failed";
  review_url?: string;
}

export interface ComplianceDoc {
  id: string;
  client_id: string;
  client_name?: string;
  kind: "service_agreement" | "croa_disclosure" | "right_to_cancel";
  content_version: string;
  signed_at?: string | null;
}

export interface StaffUser {
  id: string;
  email: string;
  name: string;
  role: "owner" | "agent" | "compliance";
  tenant_id?: string;
  is_super_admin?: boolean;
}

export interface DashboardStats {
  active_clients: number;
  onboarding: number;
  funding_ready: number;
  contracts_awaiting_signature: number;
  deals_in_pipeline: number;
  funded_this_month: number;
  amount_funded_this_month: number;
  expected_commission: number;
  brm_referrals_this_month: number;
  active_lenders: number;
}

/* ---------- auth ---------- */

export const auth = {
  // Backend returns { ok, user } and sets an HttpOnly session cookie. No token in body.
  login: async (email: string, password: string) => {
    const r = await request<{ ok: true; user: StaffUser }>("/api/auth/login", {
      method: "POST",
      body: { email, password },
      publicRoute: true,
    });
    return { user: r.user, token: "" };
  },
  me: async () => {
    const r = await request<{ ok: true; user: StaffUser }>("/api/auth/me");
    return r.user;
  },
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
};

/* ---------- dashboard ---------- */

export const dashboard = {
  stats: async () => {
    const r = await request<{ ok: true; stats: DashboardStats }>("/api/dashboard/stats");
    return r.stats;
  },
};

/* ---------- clients ---------- */

export const clients = {
  list: async (q?: { status?: string; search?: string; limit?: number; offset?: number }) => {
    // Backend's `search` param is named `q`; backend returns { ok, clients, count, limit, offset }
    const params: Record<string, string | number | undefined> = q ? { ...q } : {};
    if (q?.search !== undefined) { params.q = q.search; delete params.search; }
    const r = await request<{ ok: true; clients: ClientListItem[]; count: number; limit: number; offset: number }>(
      "/api/clients",
      { query: params as any },
    );
    return { items: r.clients ?? [], total: r.count ?? 0 };
  },
  get: async (id: string) => {
    const r = await request<{ ok: true; client: Client } | Client>(`/api/clients/${id}`);
    return (r as any).client ?? r;
  },
  create: async (input: Partial<Client>) => {
    const r = await request<{ ok: true; client: Client }>("/api/clients", { method: "POST", body: input });
    return r.client;
  },
  update: async (id: string, patch: Partial<Client>) => {
    const r = await request<{ ok: true; client: Client } | Client>(`/api/clients/${id}`, { method: "PATCH", body: patch });
    return (r as any).client ?? r;
  },
  importCsv: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<{ created: number; errors: Array<{ row: number; message: string }> }>(
      "/api/clients/import-csv",
      { method: "POST", formData: fd },
    );
  },
  // PII reveal — logs an event server-side
  revealPii: (id: string, field: "ssn" | "dob") =>
    request<{ value: string }>(`/api/clients/${id}/reveal`, {
      method: "POST",
      body: { field },
    }),

  // Save (or update) the client's Credit Karma login. Password is optional on
  // an email-only edit; sending a password re-encrypts server-side.
  saveCreditKarma: (id: string, creds: { email?: string; password?: string }) =>
    request<{ ok: true; client: Client }>(`/api/clients/${id}`, {
      method: "PATCH",
      body: {
        credit_karma_email: creds.email,
        ...(creds.password ? { credit_karma_password: creds.password } : {}),
      },
    }),

  // Reveal the stored Credit Karma login for a manual capture — audited.
  revealCreditKarma: (id: string) =>
    request<{ ok: true; field: "credit_karma"; value: { email: string | null; password: string | null } | null }>(
      `/api/clients/${id}/reveal`,
      { method: "POST", body: { field: "credit_karma" } },
    ),

  // Email the client a scoped, no-login link asking for ONLY the named
  // documents — never reopens intake, never touches their existing data.
  requestDocuments: (id: string, keys: string[], note?: string) =>
    request<{ ok: true; items: DocRequestItem[]; link: string; email_delivered: boolean; email_error: string | null }>(
      `/api/clients/${id}/request-documents`,
      { method: "POST", body: { keys, ...(note ? { note } : {}) } },
    ),

  // Reveal the stored monitoring login (MFSN/SmartCredit/IdentityIQ/Experian/…)
  // for a manual login or import — audited server-side.
  revealMonitoring: (id: string) =>
    request<{ ok: true; field: "monitoring_credentials"; value: { provider: string | null; username: string | null; password: string | null } | null }>(
      `/api/clients/${id}/reveal`,
      { method: "POST", body: { field: "monitoring_credentials" } },
    ),

  // Save (or update) the client's monitoring login. Username always applies;
  // sending a password re-encrypts it server-side (leave blank to keep).
  saveMonitoring: (id: string, creds: { username?: string; password?: string }) =>
    request<{ ok: true; client: Client }>(`/api/clients/${id}`, {
      method: "PATCH",
      body: {
        ...(creds.username !== undefined ? { monitoring_username: creds.username } : {}),
        ...(creds.password ? { monitoring_password: creds.password } : {}),
      },
    }),
  remove: (id: string) =>
    request<{ ok: true }>(`/api/clients/${id}`, { method: "DELETE" }),
  removeUpload: (clientId: string, uploadId: string) =>
    request<{ ok: true; deleted: string }>(`/api/clients/${clientId}/uploads`, { method: "DELETE", body: { upload_id: uploadId, confirm: true } }),
  /** Permanent erase — only works on already-deleted (churned) clients. */
  removeForever: (id: string) =>
    request<{ ok: true; hard: true }>(`/api/clients/${id}?hard=1`, { method: "DELETE", body: { confirm: true } }),

  // One-click: analyze latest report -> auto-pick high/critical findings ->
  // generate dispute round -> render the 3 bureau bundle PDFs -> zip.
  //
  // Returns a Blob (the zip file) on success. CROA-blocked / no-parsed-report
  // failures surface as ApiError with the structured server code (e.g.
  // 'service_agreement_unsigned', 'no_parsed_report').
  // Send the latest credit analysis to the client (or a staff-chosen test
  // address). Pure staff→client comms; nothing is mailed to the bureaus.
  // 503 → { error: 'resend_not_configured', message } when Resend is missing.
  sendAnalysis: async (
    id: string,
    body: {
      confirm: true;
      recipient_email: string;
      custom_message?: string;
      include_finding_details?: boolean;
      send_mode?: string;
    },
  ) => {
    return await request<{ ok: true; message_id: string; recipient_email: string }>(
      `/api/clients/${id}/send-analysis`,
      { method: "POST", body },
    );
  },

  // ── Service Agreement (CROA-gated) ───────────────────────────────────
  // Staff endpoints for sending / revoking a service agreement and reading
  // the current state. The portal sign flow lives in the `portal` namespace.
  sendServiceAgreement: async (
    id: string,
    body: {
      confirm: true;
      recipient_email?: string;
      /** Contract fee percent: 1–10 or 9.99 — picked per client. */
      percentage: number;
      test_mode?: boolean;
    },
  ) => {
    return await request<{
      ok: true;
      agreement_id: string;
      sign_url: string;
      recipient_email: string;
      requested_recipient: string;
      test_mode: boolean;
      sent_at: string;
      sign_token_expires_at: string;
      template_version: string;
      message_id: string;
    }>(`/api/clients/${id}/service-agreement/send`, { method: "POST", body });
  },
  revokeServiceAgreement: async (id: string) => {
    return await request<{
      ok: true;
      revoked_count: number;
      revoked: Array<{ id: string; sent_at: string | null; sent_to_email: string | null }>;
    }>(`/api/clients/${id}/service-agreement/revoke`, {
      method: "POST",
      body: { confirm: true },
    });
  },
};

/* ---------- funding: lenders / recommendations / BRM ---------- */

export interface Lender {
  id: string;
  tenant_id: string | null;
  is_platform?: boolean;
  name: string;
  lender_type: string;
  products: string[];
  description: string | null;
  application_url: string | null;
  min_credit_score: number | null;
  min_time_in_business_months: number | null;
  min_annual_revenue: number | null;
  min_amount: number | null;
  max_amount: number | null;
  allowed_states: string[];
  excluded_states: string[];
  industries_preferred: string[];
  industries_excluded: string[];
  healthcare_friendly: boolean;
  personal_credit_based: boolean;
  requires_bankruptcy_clear_years: number | null;
  status: "active" | "paused" | "closed";
  funding_speed_days: number | null;
  notes: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
}

export interface LenderMatch {
  /** Persisted lender_matches row id — outcome recording targets this. */
  id?: string;
  lender_id: string;
  lender_name: string;
  lender_type: string;
  products: string[];
  application_url: string | null;
  match_score: number;
  reasons: string[];
  warnings: string[];
  status?: string;
  amount?: number | null;
  product?: string | null;
  outcome_at?: string | null;
  outcome_note?: string | null;
}

/** Credit Karma pre-qual offer (cards carry fees; loans are blind but priced). */
export interface CkPrequalOffer {
  kind: "credit_card" | "loan";
  name: string;
  approval_odds: string;
  apr?: string | null;
  annual_fee?: string | null;
  credit_limit?: string | null;
  amount?: number | null;
  est_apr?: string | null;
  monthly_payment?: string | null;
  term_months?: number | null;
}

export interface CkPrequalTier {
  report_id: string;
  captured_at: string | null;
  prequalifications: CkPrequalOffer[];
  borrowing_potential: number | null;
  applications_in_progress: string[];
}

/** One row of the approval database — a real outcome with its date. */
export interface ApprovalRecord {
  id: string;
  status: string;
  amount: number | null;
  product: string | null;
  source: "matcher" | "manual";
  applied_at: string | null;
  outcome_at: string | null;
  outcome_note: string | null;
  created_at: string;
  lender_id: string;
  lender_name: string;
  lender_type: string;
}

export const lenders = {
  list: async (opts?: { status?: string }) => {
    const q = opts?.status ? `?status=${encodeURIComponent(opts.status)}` : "";
    const r = await request<{ ok: true; lenders: Lender[] }>(`/api/lenders${q}`);
    return r.lenders;
  },
  create: async (body: Partial<Lender> & { name: string; platform?: boolean }) => {
    const r = await request<{ ok: true; lender: Lender }>(`/api/lenders`, { method: "POST", body });
    return r.lender;
  },
  update: async (id: string, body: Partial<Lender>) => {
    const r = await request<{ ok: true; lender: Lender }>(`/api/lenders/${id}`, { method: "PATCH", body });
    return r.lender;
  },
  remove: async (id: string) => {
    return await request<{ ok: true }>(`/api/lenders/${id}`, { method: "DELETE", body: { confirm: true } });
  },
};

export interface BrmRep {
  id: string;
  name: string;
  bank_name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  is_default: boolean;
  active: boolean;
}

export const brmReps = {
  list: async () => {
    const r = await request<{ ok: true; reps: BrmRep[] }>(`/api/brm-reps`);
    return r.reps;
  },
  create: async (body: { name: string; bank_name: string; email: string; phone?: string; notes?: string; is_default?: boolean }) => {
    const r = await request<{ ok: true; rep: BrmRep }>(`/api/brm-reps`, { method: "POST", body });
    return r.rep;
  },
  update: async (id: string, body: Partial<BrmRep>) => {
    const r = await request<{ ok: true; rep: BrmRep }>(`/api/brm-reps/${id}`, { method: "PATCH", body });
    return r.rep;
  },
  remove: async (id: string) => {
    return await request<{ ok: true }>(`/api/brm-reps/${id}`, { method: "DELETE", body: { confirm: true } });
  },
};

export const recommendations = {
  generate: async (clientId: string) => {
    return await request<{
      ok: true;
      generated: number;
      funding_readiness: any;
      matches: LenderMatch[];
      credit_karma: CkPrequalTier | null;
    }>(`/api/clients/${clientId}/recommendations`, { method: "POST" });
  },
  latest: async (clientId: string) => {
    return await request<{ ok: true; matches: LenderMatch[]; credit_karma: CkPrequalTier | null }>(
      `/api/clients/${clientId}/recommendations`,
    );
  },
};

export const lenderMatches = {
  recordOutcome: async (
    matchId: string,
    body: { status: string; amount?: number | string; product?: string; outcome_on?: string; note?: string },
  ) => {
    const r = await request<{ ok: true; match: any }>(`/api/lender-matches/${matchId}`, {
      method: "PATCH",
      body,
    });
    return r.match;
  },
};

export const approvals = {
  list: async (clientId: string) => {
    const r = await request<{ ok: true; approvals: ApprovalRecord[] }>(`/api/clients/${clientId}/approvals`);
    return r.approvals;
  },
  /** Screenshot intake: data-URL images in → extracted draft rows out (nothing saved). */
  extract: async (clientId: string, images: string[]) => {
    return await request<{
      ok: true;
      rows: Array<{ lender_name: string; amount: number | null; product: string; approved_on: string | null; status: string; note: string | null; source_image: number }>;
      warnings: string[];
    }>(`/api/clients/${clientId}/approvals/extract`, { method: "POST", body: { images } });
  },
  bulkAdd: async (
    clientId: string,
    rows: Array<{ lender_name: string; amount?: string | number; product?: string; approved_on: string; status?: string; note?: string }>,
  ) => {
    return await request<{ ok: true; created: any[] }>(`/api/clients/${clientId}/approvals`, {
      method: "POST",
      body: { approvals: rows },
    });
  },
};

export const brm = {
  send: async (clientId: string, body: { confirm: true; brm_rep_id?: string; note?: string }) => {
    return await request<{
      ok: true;
      referral_id: string;
      sent_at: string;
      rep: { id: string; name: string; bank_name: string; email: string };
      message_id: string;
    }>(`/api/clients/${clientId}/send-to-brm`, { method: "POST", body });
  },
};

/* ---------- authenticated file viewing ---------- */

// A plain <a href> to a protected /api/uploads/... URL cannot carry the staff
// bearer token, so a new tab arrives unauthenticated and gets
// {"error":"unauthorized"}. Fetch the bytes WITH the token, then open them.
export async function openAuthenticatedFile(path: string, filename?: string): Promise<void> {
  const token = typeof window !== "undefined" ? localStorage.getItem("nedm.staff.token") : null;
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    let msg = `Could not open file (HTTP ${res.status})`;
    try { const j = await res.json(); msg = j?.message || j?.error || msg; } catch { /* keep default */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "noopener");
  if (!w) {
    // Popup blocked — fall back to a download.
    const a = document.createElement("a");
    a.href = url; a.download = filename || path.split("/").pop() || "file";
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* ---------- funding applications (recommendations / softpull / packets / builder) ---------- */

export const fundingApps = {
  recommendations: async (clientId: string, path?: "personal" | "business" | "both", onlineOnly?: boolean) => {
    const params = new URLSearchParams();
    if (path) params.set("path", path);
    if (onlineOnly) params.set("online", "1");
    const q = params.toString() ? `?${params}` : "";
    return await request<{ ok: true; analyzed: boolean; preference: string | null; results: any[]; message?: string }>(
      `/api/clients/${clientId}/product-recommendations${q}`);
  },
  softpull: async (clientId: string) => {
    return await request<{ ok: true; report_id: string; scores: Record<string, number>; bureaus: any[]; intelligence: any }>(
      `/api/clients/${clientId}/softpull`, { method: "POST", body: { confirm: true } });
  },
  softpullTest: async () => {
    return await request<{ ok: true; bureaus: any[]; scores: Record<string, number>; test_applicant: string }>(
      `/api/integrations/softpull-test`, { method: "POST", body: {} });
  },
  packetHistory: async (clientId: string) => {
    return await request<{ ok: true; packets: any[] }>(`/api/clients/${clientId}/lender-packet`);
  },
  sendPacket: async (clientId: string, body: { confirm: true; lender_id?: string; to_email: string; doc_ids?: string[]; reviewed_doc_ids?: string[]; acknowledged_sensitive_doc_ids?: string[]; note?: string }) => {
    return await request<{ ok: true; packet: { id: string; sent_to: string; attached: number } }>(
      `/api/clients/${clientId}/lender-packet`, { method: "POST", body });
  },
  builder: async (clientId: string) => {
    return await request<{ ok: true; initialized: boolean; progress_pct: number; phase_labels: Record<string, string>; steps: any[] }>(
      `/api/clients/${clientId}/builder`);
  },
  builderInit: async (clientId: string) => {
    return await request<{ ok: true }>(`/api/clients/${clientId}/builder`, { method: "POST", body: { action: "init" } });
  },
  builderToggle: async (clientId: string, stepId: string, done: boolean) => {
    return await request<{ ok: true }>(`/api/clients/${clientId}/builder`, { method: "POST", body: { action: "toggle", step_id: stepId, done } });
  },
  requestDetails: async (clientId: string, body?: { fields?: string[]; note?: string; days?: number }) => {
    return await request<{ ok: true; fields: string[]; link: string; email_delivered: boolean; email_error: string | null }>(
      `/api/clients/${clientId}/request-details`, { method: "POST", body: body || {} });
  },
  repairStatus: async (clientId: string) => {
    return await request<{ ok: true; tagged_at: string | null; handoff_at: string | null; handoff: any }>(
      `/api/clients/${clientId}/credit-repair`);
  },
  repairTag: async (clientId: string, tag: boolean) => {
    return await request<{ ok: true; tagged: boolean }>(
      `/api/clients/${clientId}/credit-repair`, { method: "POST", body: { action: tag ? "tag" : "untag" } });
  },
  repairHandoff: async (clientId: string, note?: string) => {
    return await request<{ ok: true; handoff: { dm_client_id: string; created: boolean } }>(
      `/api/clients/${clientId}/credit-repair`, { method: "POST", body: { action: "handoff", confirm: true, note } });
  },
  uploads: async (clientId: string) => {
    return await request<{ ok: true; uploads: any[] }>(`/api/clients/${clientId}/uploads`);
  },
};

export const creditProducts = {
  list: async () => {
    return await request<{ ok: true; products: any[] }>("/api/products");
  },
  create: async (body: Record<string, any>) => {
    return await request<{ ok: true; product: { id: string; name: string } }>("/api/products", { method: "POST", body });
  },
  update: async (id: string, body: Record<string, any>) => {
    return await request<{ ok: true }>(`/api/products/${id}`, { method: "PATCH", body });
  },
  remove: async (id: string) => {
    return await request<{ ok: true }>(`/api/products/${id}`, { method: "DELETE", body: { confirm: true } });
  },
};

/* ---------- intake invitations (staff side) ---------- */

export interface DocRequestItem { key: string; label: string; }
export interface DocRequestPublic {
  ok: true;
  first_name: string;
  company_name: string;
  items: DocRequestItem[];
  note: string | null;
  expires_at: string;
  completed: boolean;
  expired: boolean;
}

// Public, token-authenticated documents-only upload flow. No login, no client
// data — the page shows only the requested document list.
export const documentRequests = {
  get: (token: string) =>
    request<DocRequestPublic>(`/api/document-requests/${token}`, { publicRoute: true }),
  upload: (token: string, key: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("key", key);
    return request<{ ok: true; key: string; stored_as: string; bytes: number }>(
      `/api/document-requests/${token}/upload`,
      { method: "POST", formData: fd, publicRoute: true },
    );
  },
};

export const REQUESTABLE_DOCS: DocRequestItem[] = [
  { key: "id_front", label: "Front of ID" },
  { key: "id_back", label: "Back of ID" },
  { key: "articles_of_incorporation", label: "Articles of Incorporation" },
  { key: "ein_letter", label: "EIN letter (IRS)" },
  { key: "ss_card", label: "Social Security card" },
  { key: "proof_of_address", label: "Proof of address" },
  { key: "bank_statement", label: "Bank statement" },
  { key: "voided_check", label: "Voided check" },
  { key: "business_license", label: "Business license" },
];

export const intakeInvitations = {
  send: async (clientId: string, channel: "sms" | "email" | "manual_link", deliveredTo: string) => {
    const r = await request<{ ok: true; invitation: IntakeInvitation }>("/api/intake-invitations", {
      method: "POST",
      body: { client_id: clientId, delivery_channel: channel, delivered_to: deliveredTo },
    });
    // Creating the invitation does NOT deliver it — /api/intake-invitations only
    // writes the row. For email/SMS the link must actually be SENT, or staff see
    // "sent" and the client never receives anything (the bug this fixes).
    if (channel === "email" || channel === "sms") {
      try {
        const sent = await request<{ ok: true; result?: any }>("/api/intake/send", {
          method: "POST",
          body: { invitation_id: r.invitation.id, channel },
        });
        return { ...r.invitation, delivery_result: (sent as any)?.result ?? null } as IntakeInvitation;
      } catch (e: any) {
        // Surface the failure instead of reporting a phantom success.
        throw new Error(
          `The intake link was created but could NOT be ${channel === "email" ? "emailed" : "texted"}: ${e?.message || "send failed"}. Copy the link and send it manually.`,
        );
      }
    }
    return r.invitation;
  },
  listForClient: async (clientId: string) => {
    const r = await request<{ ok: true; invitations: IntakeInvitation[]; count: number; limit: number; offset: number }>(
      "/api/intake-invitations",
      { query: { client_id: clientId } },
    );
    return r.invitations ?? [];
  },
};

/* ---------- business funding application (optional 2nd intake form) ---------- */

export const businessApplications = {
  // Create + email the business-funding application link to a client.
  send: async (clientId: string, deliveredTo?: string) => {
    return await request<{ ok: true; application: { token: string; apply_url: string; expires_at: string }; email: { mode: string } }>(
      "/api/business-applications",
      { method: "POST", body: { client_id: clientId, channel: "email", delivered_to: deliveredTo } },
    );
  },
  listForClient: async (clientId: string) => {
    const r = await request<{ ok: true; applications: Array<{ id: string; token: string; status: string; apply_url: string; business_name: string | null; submitted_at: string | null; created_at: string }> }>(
      "/api/business-applications",
      { query: { client_id: clientId } },
    );
    return r.applications ?? [];
  },
  // Full decrypted payload for staff view. SSNs masked unless reveal=true.
  detail: async (id: string, reveal = false) => {
    return await request<{
      ok: true;
      application: { id: string; client_id: string; client_name: string; status: string; business_name: string | null; submitted_at: string | null; created_at: string };
      payload: Record<string, any>;
      revealed: boolean;
    }>(`/api/business-applications/detail/${id}`, { query: reveal ? { reveal: "1" } : undefined });
  },
  // Merge edited fields into the stored payload (owner/manager only).
  update: async (id: string, payload: Record<string, any>) => {
    return await request<{ ok: true }>(`/api/business-applications/detail/${id}`, { method: "PATCH", body: { payload } });
  },
};

/* ---------- public intake (uses token, not auth) ---------- */

export interface IntakeContext {
  invitation: IntakeInvitation;
  client: {
    id: string;
    legal_name: string;
    email: string;
    phone: string;
  };
  steps_complete: {
    identity: boolean;
    funding: boolean;
    documents: boolean;
    monitoring: boolean;
  };
  /** The autosaved (non-secret) draft — used to prefill on resume. */
  draft: Record<string, any>;
  /** Doc kinds already uploaded in an earlier session (r2 refs restored). */
  restored_documents: string[];
  // Tenant affiliate enroll links (shown on the monitoring step for clients
  // without credit monitoring — signups run through the tenant's link).
  mfsn_affiliate_url: string | null;
  smartcredit_affiliate_url: string | null;
  /** FCRA authorization text, served by the API so what the client reads is
   *  exactly the version recorded against their signature. */
  credit_authorization: {
    version: string; title: string; intro: string; points: string[];
    signature_prompt: string; footer: string;
  } | null;
}

// In-memory state for an in-progress intake. The form holds onto these
// between steps so the final submit can ship the whole bundle.
const _intakeBundles: Record<string, {
  identity?: any;
  funding?: any;
  monitoring?: any;
  documents: Record<string, string>;
  credit_authorization?: { accepted: boolean; signed_name: string };
}> = {};

function _bundle(token: string) {
  if (!_intakeBundles[token]) _intakeBundles[token] = { documents: {} };
  return _intakeBundles[token];
}

export const intake = {
  // GET /api/intake/:token — backend returns { ok, intake: { token, client, draft, expires_at } }.
  // Unwrap and synthesize the IntakeContext shape the page reads.
  get: async (token: string): Promise<IntakeContext> => {
    const r = await request<{ ok: true; intake: any }>(`/api/intake/${token}`, { publicRoute: true });
    const i = r.intake ?? {};
    return {
      invitation: {
        id: i.id ?? "",
        client_id: i.client_id ?? "",
        token: i.token ?? token,
        delivery_channel: "manual_link",
        delivered_to: "",
        expires_at: i.expires_at ?? "",
        started_at: i.started_at ?? null,
        completed_at: i.completed_at ?? null,
        created_at: i.created_at ?? "",
      },
      client: {
        id: i.client?.id ?? "",
        legal_name: i.client?.legal_name ?? "",
        email: i.client?.email ?? "",
        phone: i.client?.phone ?? "",
      },
      draft: i.draft ?? {},
      // Restore what SAFELY survives a new browser session. Uploaded documents
      // are autosaved as r2:// refs (docs_meta) and are re-seeded into the
      // bundle here. Secrets (SSN, passwords) are deliberately NEVER autosaved,
      // so a step only counts complete if its secret is in THIS session's
      // memory — a resumed client re-enters those, with everything else
      // prefilled from the draft.
      restored_documents: Object.keys(i.draft?.docs_meta ?? {}),
      steps_complete: (() => {
        const b = _bundle(token);
        if (i.draft?.docs_meta) b.documents = { ...i.draft.docs_meta, ...b.documents };
        return {
          identity:    !!b.identity?.ssn_full,
          funding:     !!i.draft?.funding,
          documents:   Object.keys(b.documents).length >= 1,
          monitoring:  !!b.monitoring?.password,
        };
      })(),
      mfsn_affiliate_url: i.mfsn_affiliate_url ?? null,
      smartcredit_affiliate_url: i.smartcredit_affiliate_url ?? null,
      credit_authorization: i.credit_authorization ?? null,
    };
  },

  // The FCRA signature from the review step. Deliberately NOT autosaved — like
  // the SSN and the monitoring password it lives only in this session's memory,
  // so a signature is always something the client typed just now.
  setCreditAuthorization: (token: string, signed_name: string) => {
    _bundle(token).credit_authorization = { accepted: true, signed_name };
  },

  // Each step autosaves to the SINGLE backend endpoint POST /api/intake/:token
  // (the per-step routes /identity, /agreements, /monitoring, /documents do
  // NOT exist on the backend — the backend uses one autosave endpoint that
  // takes any of the allowed keys). The form also remembers its state in
  // a module-local bundle so submit() can ship everything at once.
  saveIdentity: async (token: string, identity: {
    legal_name: string; dob: string; ssn_full: string;
    address_line1: string; address_city: string; address_state: string; address_zip: string;
    phone?: string;
  }) => {
    _bundle(token).identity = identity;
    return request<{ ok: true }>(`/api/intake/${token}`, {
      method: "POST",
      // Don't autosave the raw SSN — keep it in client memory until final submit.
      body: { identity: { ...identity, ssn_full: undefined } },
      publicRoute: true,
    });
  },

  // Funding questions: how much capital, the goal, what the business is,
  // and where it's located (lenders are location-restricted).
  saveFunding: async (token: string, funding: {
    capital_requested: number; use_of_funds: string;
    funding_preference?: "personal" | "business" | "both";
    annual_income?: number; income_source?: string; employer_name?: string;
    employer_address?: string; employer_phone?: string;
    employment_years?: number; employment_months?: number;
    other_income?: Array<{ source: string; amount: number; frequency: "monthly" | "annual" }>;
    checking_balance?: number; savings_balance?: number;
    housing_status?: string; housing_payment?: number;
    business_description?: string;
    business_state?: string; business_name?: string; business_entity_type?: string;
    business_ein?: string; business_industry?: string; business_city?: string;
    business_zip?: string; business_start_date?: string;
    annual_revenue?: number; monthly_revenue?: number;
  }) => {
    _bundle(token).funding = funding;
    return request<{ ok: true }>(`/api/intake/${token}`, {
      method: "POST",
      body: { funding },
      publicRoute: true,
    });
  },

  // Document upload — POSTs the file as multipart/form-data to the public
  // intake upload route; backend writes to Cloudflare R2 and returns the
  // canonical r2:// object URL that the final submit() ships in
  // identity_documents[].
  uploadDocument: async (
    token: string,
    kind: "government_id" | "sos_document" | "ein_letter" | "bank_statement" | "voided_check" | "tax_return",
    file: File,
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const r = await request<{ ok: true; file_url: string; kind: string; bytes: number }>(
      `/api/intake/${token}/upload`,
      { method: "POST", formData: fd, publicRoute: true },
    );
    _bundle(token).documents[kind] = r.file_url;
    // Autosave the r2:// refs (not sensitive) so a resumed session doesn't
    // force the client to re-upload documents that already landed in R2.
    request<{ ok: true }>(`/api/intake/${token}`, {
      method: "POST",
      body: { docs_meta: _bundle(token).documents },
      publicRoute: true,
    }).catch(() => { /* best-effort; submit still carries the bundle */ });
    return r;
  },

  saveMonitoring: async (token: string, monitoring: {
    provider: Exclude<MonitoringProvider, "not_started">; username: string; password: string;
    credit_karma?: { email: string; password: string };
  }) => {
    _bundle(token).monitoring = monitoring;
    return request<{ ok: true }>(`/api/intake/${token}`, {
      method: "POST",
      // Don't autosave the monitoring password — keep in memory until submit.
      body: { monitoring_provider: monitoring.provider },
      publicRoute: true,
    });
  },

  // Final submit ships the full bundle to the backend submit endpoint.
  // The backend validates + persists everything in one go (encrypts SSN
  // and monitoring creds server-side, inserts agreements with IP/UA).
  submit: async (token: string) => {
    const b = _bundle(token);
    const identityIn = b.identity || {};
    const fundingIn = b.funding || {};
    const monitoringIn = b.monitoring || {};
    const body = {
      identity: {
        legal_name: identityIn.legal_name,
        dob: identityIn.dob,
        ssn: identityIn.ssn_full,
        address_line1: identityIn.address_line1,
        address_city: identityIn.address_city,
        address_state: identityIn.address_state,
        address_zip: identityIn.address_zip,
        phone: identityIn.phone,
      },
      funding: fundingIn,
      identity_documents: Object.entries(b.documents).map(([kind, file_url]) => ({ kind, file_url })),
      credit_karma: monitoringIn.credit_karma?.email
        ? { email: monitoringIn.credit_karma.email, password: monitoringIn.credit_karma.password }
        : undefined,
      monitoring: monitoringIn.provider
        ? { provider: monitoringIn.provider, username: monitoringIn.username, password: monitoringIn.password }
        : undefined,
      credit_authorization: b.credit_authorization,
    };
    const result = await request<{ ok: true; message: string }>(`/api/intake/${token}/submit`, {
      method: "POST",
      body,
      publicRoute: true,
    });
    delete _intakeBundles[token];
    return result;
  },

};

/* ---------- credit reports ---------- */

export const reports = {
  list: async (clientId?: string) => {
    const r = await request<{ ok: true; reports: CreditReport[]; count: number; limit: number; offset: number }>(
      "/api/credit-reports",
      { query: { client_id: clientId } },
    );
    return r.reports ?? [];
  },
  // NOTE: File upload to R2 is deferred. For now we POST a JSON record with
  // file_url:'(pending)' as a placeholder so the report row exists; the actual
  // PDF upload will be wired through R2 in a follow-up.
  upload: async (clientId: string, _file: File) => {
    const r = await request<{ ok: true; report: CreditReport }>("/api/credit-reports", {
      method: "POST",
      body: {
        client_id: clientId,
        provider: "manual_upload",
        file_url: "(pending)",
      },
    });
    return r.report;
  },
  // Upload a client's credit-report PDF (myFICO / Experian / Credit Karma / etc).
  // Text is extracted in the browser (src/lib/pdf-extract) and posted here with
  // the raw PDF; the server parses scores + signals and runs the analyzer.
  remove: async (id: string) =>
    request<{ ok: true; deleted: string }>(`/api/credit-reports/${id}`, { method: "DELETE", body: { confirm: true } }),
  uploadPdf: async (clientId: string, args: { text: string; pdf_base64?: string; filename?: string }) => {
    return await request<{
      ok: true; report: CreditReport; provider_label: string;
      scores?: { equifax?: number; experian?: number; transunion?: number };
      signals?: Record<string, number>; warnings?: string[];
    }>("/api/credit-reports/upload-pdf", {
      method: "POST",
      body: { client_id: clientId, ...args },
    });
  },
  importFromCredentials: async (clientId: string) => {
    // Returns the whole envelope so the UI can show scores when present.
    return await request<{
      ok: true;
      report: CreditReport;
      scores?: { equifax?: number; experian?: number; transunion?: number };
      captured_at?: string;
      final_url?: string;
    }>("/api/credit-reports/import", {
      method: "POST",
      body: { client_id: clientId },
    });
  },
  // Run the FCRA analyzer over the captured HTML for this report. Persists
  // analysis_json + analyzed_at server-side and echoes the analysis back.
  analyze: async (reportId: string) => {
    const r = await request<{ ok: true; analysis: any }>(
      `/api/credit-reports/${reportId}/analyze`,
      { method: "POST" },
    );
    return r.analysis;
  },
  // Read the previously-stored analysis. Throws 404 if not analyzed yet,
  // letting the caller distinguish "no analysis" from a real error.
  getAnalysis: async (reportId: string) => {
    const r = await request<{ ok: true; analysis: any }>(
      `/api/credit-reports/${reportId}/analysis`,
    );
    return r.analysis;
  },
  // Run the MFSN parser over the captured HTML (read-only — does not persist).
  // Returns the parsed-report shape so the UI can render a viewer modal.
  getParsed: async (reportId: string) => {
    return await request<{ ok: true; report: CreditReport; parsed: any }>(
      `/api/credit-reports/${reportId}/parsed`,
    );
  },
};

/* ---------- disputes ---------- */

export const disputes = {
  list: async (q?: { client_id?: string; status?: string }) => {
    const r = await request<{ ok: true; rounds: DisputeRound[]; count: number; limit: number; offset: number }>(
      "/api/dispute-rounds",
      { query: q },
    );
    return r.rounds ?? [];
  },
  get: async (id: string) => {
    const r = await request<{ ok: true; round: DisputeRound }>(`/api/dispute-rounds/${id}`);
    return r.round;
  },
  create: async (clientId: string) => {
    const r = await request<{ ok: true; round: DisputeRound }>("/api/dispute-rounds", {
      method: "POST",
      body: { client_id: clientId },
    });
    return r.round;
  },
  advance: async (id: string, status: DisputeRound["status"]) => {
    const r = await request<{ ok: true; round: DisputeRound }>(`/api/dispute-rounds/${id}`, {
      method: "PATCH",
      body: { status },
    });
    return r.round;
  },
  // Materialize a round + draft letters from a set of checked analyzer findings.
  // Backend groups findings by (suggested_letter, recipient_bureau) into one
  // letter per group.
  createFromAnalysis: async (input: {
    credit_report_id: string;
    finding_ids: string[];
    round_notes?: string;
  }) => {
    const r = await request<{ ok: true; round: any; letters: any[] }>(
      "/api/dispute-rounds/from-analysis",
      { method: "POST", body: input },
    );
    return r;
  },
};

/* ---------- letters ---------- */

export const letters = {
  list: async (q?: { client_id?: string; status?: string; round_id?: string }) => {
    const r = await request<{ ok: true; letters: Letter[]; count: number; limit: number; offset: number }>(
      "/api/letters",
      { query: q },
    );
    return r.letters ?? [];
  },
  create: async (input: { client_id: string; kind: Letter["kind"]; body_text?: string; dispute_round_id?: string }) => {
    const r = await request<{ ok: true; letter: Letter }>("/api/letters", { method: "POST", body: input });
    return r.letter;
  },
  preview: async (id: string) => {
    const r = await request<{
      ok: true;
      pdf_url: string | null;
      body_text?: string;
      recipient_address?: string;
      recipient_bureau?: string;
      kind?: string;
      status?: string;
    }>(`/api/letters/${id}/preview`);
    return {
      pdf_url: r.pdf_url ?? null,
      body_text: r.body_text ?? "",
      recipient_address: r.recipient_address ?? "",
      recipient_bureau: r.recipient_bureau,
      kind: r.kind,
      status: r.status,
    };
  },
  get: async (id: string) => {
    const r = await request<{ ok: true; letter: Letter & { body_text?: string; recipient_address?: string; quality_gate_notes?: string | null; approved_at?: string | null } }>(`/api/letters/${id}`);
    return r.letter;
  },
  patch: async (
    id: string,
    body: { status?: string; body_text?: string; recipient_address?: string; quality_gate_notes?: string },
  ) => {
    const r = await request<{ ok: true; letter: Letter & { body_text?: string; recipient_address?: string } }>(`/api/letters/${id}`, {
      method: "PATCH",
      body,
    });
    return r.letter;
  },
};

/* ---------- mail ---------- */

export interface MailListResponse {
  jobs: MailJob[];
  providers: MailProviderInfo[];
  count: number;
  limit: number;
  offset: number;
}

export const mail = {
  list: async (q?: {
    status?: string;
    provider?: string;
    client_id?: string;
    round_id?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<MailListResponse> => {
    const r = await request<{
      ok: true;
      jobs: MailJob[];
      providers?: MailProviderInfo[];
      count: number;
      limit: number;
      offset: number;
    }>("/api/mail-jobs", { query: q });
    return {
      jobs: r.jobs ?? [],
      providers: r.providers ?? [],
      count: r.count ?? 0,
      limit: r.limit ?? 50,
      offset: r.offset ?? 0,
    };
  },
  // Queue a single-letter mail job. Backend requires confirm:true on mutation.
  queue: async (
    letterId: string,
    mail_type: "first_class" | "certified",
    provider: MailProviderId = "lob",
  ) => {
    const r = await request<{ ok: true; job: MailJob }>("/api/mail-jobs", {
      method: "POST",
      body: { letter_id: letterId, mail_type, provider, confirm: true },
    });
    return r.job;
  },
  // Dispatch a queued job to its provider.
  send: async (id: string) => {
    const r = await request<{ ok: true; job: MailJob; result?: any }>(
      `/api/mail-jobs/${id}/send`,
      { method: "POST", body: { confirm: true } },
    );
    return r.job;
  },
  // Cancel a queued job (no provider call).
  cancel: async (id: string) => {
    const r = await request<{ ok: true; job: MailJob }>(
      `/api/mail-jobs/${id}/cancel`,
      { method: "POST", body: { confirm: true } },
    );
    return r.job;
  },
  // Poll the provider for a status update on a sent job.
  status: async (id: string) => {
    const r = await request<{ ok: true; job: MailJob; polled?: any }>(
      `/api/mail-jobs/${id}/status`,
    );
    return r.job;
  },
  // Bulk-dispatch multiple queued jobs in one call.
  bulkSend: async (ids: string[]) => {
    const r = await request<{
      ok: true;
      sent: number;
      failed: number;
      results: Array<{ id: string; ok: boolean; status?: string; code?: string; error?: string }>;
    }>("/api/mail-jobs/bulk-send", {
      method: "POST",
      body: { ids, confirm: true },
    });
    return r;
  },
  // Render + send a per-bureau bundle as a single mail-job.
  sendBundle: async (args: {
    round_id: string;
    bureau: "experian" | "transunion" | "equifax";
    provider: MailProviderId;
    mail_type: "first_class" | "certified";
  }) => {
    const r = await request<{
      ok: true;
      mail_job_id: string;
      provider: MailProviderId;
      provider_id: string;
      status: MailJobStatus;
      tracking_url?: string | null;
      expected_delivery?: string | null;
    }>("/api/mail-jobs/bundle", {
      method: "POST",
      body: { ...args, confirm: true },
    });
    return r;
  },
};

/* ---------- filings (FTC / CFPB) ---------- */

export const filings = {
  list: async (clientId?: string) => {
    const r = await request<{ ok: true; filings: Filing[]; count: number; limit: number; offset: number }>(
      "/api/filings",
      { query: { client_id: clientId } },
    );
    return r.filings ?? [];
  },
  prepare: async (clientId: string, agency: Filing["agency"]) => {
    const r = await request<{ ok: true; filing: Filing }>("/api/filings", {
      method: "POST",
      body: { client_id: clientId, agency, kind: agency, draft_content: {} },
    });
    return r.filing;
  },
};

/* ---------- compliance ---------- */

export const compliance = {
  list: async (clientId?: string) => {
    const r = await request<{ ok: true; docs: ComplianceDoc[]; count: number; limit: number; offset: number }>(
      "/api/compliance-docs",
      { query: { client_id: clientId } },
    );
    return r.docs ?? [];
  },
};

/* ---------- client portal ---------- */

export interface PortalClient {
  id: string;
  legal_name: string;
  email: string;
}

export interface PortalProgress {
  client?: PortalClient;
  score_start?: number | null;
  score_current?: number | null;
  rounds?: Array<{
    id: string;
    round_number: number;
    status: string;
    bureau: string;
    items_count: number;
    created_at: string;
    due_date?: string | null;
  }>;
  letters?: Array<{
    id: string;
    kind: string;
    bureau?: string | null;
    recipient?: string | null;
    status: string;
    created_at: string;
  }>;
  responses?: Array<{
    id: string;
    uploaded_at: string;
    original_filename: string;
    source_hint?: string | null;
    analyzed: boolean;
  }>;
}

export interface CorrespondenceUpload {
  id: string;
  client_id: string;
  original_filename: string;
  content_type: string;
  bytes: number;
  uploaded_at: string;
  uploaded_via?: string;
  client_note?: string | null;
  source_hint?: string | null;
  analyzed?: boolean;
  analysis_id?: string | null;
}

export interface CorrespondenceAnalysis {
  id?: string;
  correspondence_id?: string;
  analyzed_at?: string;
  ai_model?: string;
  bureau?: string | null;
  sender?: string | null;
  response_classification?: string | null;
  items_mentioned?: Array<{ creditor?: string; account_last4?: string | null; status?: string | null }> | null;
  recommended_action?: string | null;
  confidence?: string | null;
  summary?: string | null;
  raw_response?: string | null;
}

export interface PortalAgreement {
  ok: true;
  agreement_id: string;
  client_name: string;
  template_version: string;
  agreement_html: string;
  status: "pending" | "signed" | "expired" | "revoked";
  sent_at: string | null;
  sent_to_email: string | null;
  expires_at: string | null;
  signed_at: string | null;
  signed_by_name: string | null;
}

export const portal = {
  requestMagicLink: async (email: string) => {
    return request<{ ok: true }>("/api/portal/request-magic-link", {
      method: "POST",
      body: { email },
      publicRoute: true,
    });
  },
  login: async (email: string, password: string) => {
    return request<{ ok: true }>("/api/portal/login", {
      method: "POST",
      body: { email, password },
      publicRoute: true,
    });
  },
  setPassword: async (token: string, password: string) => {
    return request<{ ok: true }>("/api/portal/set-password", {
      method: "POST",
      body: { token, password },
      publicRoute: true,
    });
  },
  requestPasswordSetup: async (email: string) => {
    return request<{ ok: true }>("/api/portal/request-password-setup", {
      method: "POST",
      body: { email },
      publicRoute: true,
    });
  },
  me: async () => {
    const r = await request<{ ok: true; client: PortalClient }>("/api/portal/me");
    return r.client;
  },
  logout: () => request<{ ok: true }>("/api/portal/logout", { method: "POST" }),
  getProgress: async () => {
    // The endpoint wraps the payload as { ok, progress: {...} } — unwrap it so
    // callers get PortalProgress directly. (Reading it flat left score_start /
    // score_current undefined and silently defaulted rounds/letters to empty.)
    const r = await request<{ ok: true; progress: PortalProgress }>("/api/portal/progress");
    return r.progress;
  },
  listCorrespondence: async () => {
    const r = await request<{ ok: true; correspondence: CorrespondenceUpload[]; count: number }>(
      "/api/portal/correspondence",
    );
    return r.correspondence ?? [];
  },
  // Service-agreement sign flow — token IS the auth, no session cookie needed.
  getAgreement: async (token: string) => {
    return await request<PortalAgreement>(`/api/portal/agreement`, {
      query: { token },
      publicRoute: true,
    });
  },
  signAgreement: async (token: string, typed_name: string, agree_checkbox: true) => {
    return await request<{
      ok: true;
      agreement_id: string;
      signed_at: string;
      confirmation_message_id: string | null;
      staff_notification_message_id: string | null;
    }>(`/api/portal/agreement/sign`, {
      method: "POST",
      body: { token, typed_name, agree_checkbox },
      publicRoute: true,
    });
  },

  uploadCorrespondence: async (file: File, sourceHint?: string, note?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    if (sourceHint) fd.append("source_hint", sourceHint);
    if (note) fd.append("client_note", note);
    const r = await request<{ ok: true; correspondence: CorrespondenceUpload }>(
      "/api/portal/correspondence",
      { method: "POST", formData: fd },
    );
    return r.correspondence;
  },
};

/* ---------- correspondence (staff) ---------- */

export const correspondence = {
  listForClient: async (clientId: string) => {
    const r = await request<{ ok: true; correspondence: CorrespondenceUpload[]; count: number }>(
      "/api/correspondence",
      { query: { client_id: clientId } },
    );
    return r.correspondence ?? [];
  },
  analyze: async (id: string) => {
    const r = await request<{ ok: true; analysis: CorrespondenceAnalysis }>(
      `/api/correspondence/${id}/analyze`,
      { method: "POST" },
    );
    return r.analysis;
  },
  getAnalysis: async (id: string) => {
    const r = await request<{ ok: true; analysis: CorrespondenceAnalysis }>(
      `/api/correspondence/${id}/analysis`,
    );
    return r.analysis;
  },
  fileUrl: (id: string) => `/api/correspondence/${id}/file`,
};

/* ---------- billing ---------- */

export type InvoiceStatus = "draft" | "sent" | "paid" | "void" | "refunded";

export interface CompletedWorkRef {
  kind: "dispute_round" | "letter" | "milestone";
  id?: string | null;
  label: string;
}

export interface InvoiceLineItem {
  description: string;
  amount_cents: number;
  completed_work_ref: CompletedWorkRef;
}

export interface Invoice {
  id: string;
  client_id: string;
  client_name?: string | null;
  client_email?: string | null;
  status: InvoiceStatus;
  total_cents: number;
  currency: string;
  description: string | null;
  line_items_json: InvoiceLineItem[];
  paid_via_processor: string | null;
  processor_charge_id: string | null;
  created_at: string;
  sent_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  refunded_at: string | null;
  due_at: string | null;
}

export interface ProcessorListItem {
  id: string;
  name: string;
  description: string;
  supports_hosted_checkout: boolean;
  supports_direct_charge: boolean;
  is_configured: boolean;
  has_db_credentials: boolean;
  is_active: boolean;
  credential_rows: Array<{
    label: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }>;
}

export interface BillingSummary {
  invoice_count: number;
  draft_count: number;
  sent_count: number;
  paid_count: number;
  void_count: number;
  refunded_count: number;
  outstanding_cents: number;
  collected_cents: number;
  last_invoice_at: string | null;
}

/* ---------- contract templates (customizable contracts) ---------- */

export interface ContractTemplate {
  id: string;
  name: string;
  description: string | null;
  body_html: string;
  backend_percentage: number | null;
  jurisdiction_state: string | null;
  language: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export const contractTemplates = {
  list: async () => {
    const r = await request<{ ok: true; templates: ContractTemplate[] }>("/api/contract-templates");
    return r.templates;
  },
  create: async (body: Partial<ContractTemplate> & { name: string; body_html: string }) => {
    const r = await request<{ ok: true; template: ContractTemplate }>("/api/contract-templates", { method: "POST", body });
    return r.template;
  },
  update: async (id: string, body: Partial<ContractTemplate>) => {
    const r = await request<{ ok: true; template: ContractTemplate }>(`/api/contract-templates/${id}`, { method: "PATCH", body });
    return r.template;
  },
  remove: async (id: string) => request<{ ok: true }>(`/api/contract-templates/${id}`, { method: "DELETE", body: { confirm: true } }),
  /** Open a rendered preview (sample client data, real document shell) in a new tab. */
  openPreview: async (body: { body_html: string; name?: string; percentage?: number | null; jurisdiction_state?: string | null }) => {
    const token = typeof window !== "undefined" ? localStorage.getItem("nedm.staff.token") : null;
    const res = await fetch("/api/contract-templates/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let msg = `Preview failed (HTTP ${res.status})`;
      try { const j = await res.json(); msg = j?.message || j?.error || msg; } catch { /* keep */ }
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "noopener");
    if (!w) { const a = document.createElement("a"); a.href = url; a.download = "contract-preview.html"; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

export const billing = {
  listProcessors: async () => {
    const r = await request<{ ok: true; processors: ProcessorListItem[]; future_processors: string[] }>(
      "/api/billing/processors",
    );
    return { processors: r.processors ?? [], future_processors: r.future_processors ?? [] };
  },
  saveProcessorCredentials: async (
    processor: string,
    payload: Record<string, unknown>,
    opts?: { label?: string; activate?: boolean },
  ) => {
    return request<{
      ok: true;
      processor: string;
      credential: { id: string; label: string | null; is_active: boolean; created_at: string; updated_at: string };
    }>(`/api/billing/processors/${processor}/credentials`, {
      method: "POST",
      body: { confirm: true, payload, label: opts?.label, activate: opts?.activate ?? true },
    });
  },
  listInvoices: async (q?: { client_id?: string; status?: InvoiceStatus | "" }) => {
    const r = await request<{ ok: true; invoices: Invoice[]; count: number }>("/api/billing/invoices", {
      query: q as any,
    });
    return r.invoices ?? [];
  },
  createInvoice: async (input: {
    client_id: string;
    line_items: InvoiceLineItem[];
    description?: string;
    due_at?: string;
    currency?: string;
  }) => {
    const r = await request<{ ok: true; invoice: Invoice }>("/api/billing/invoices", {
      method: "POST",
      body: { confirm: true, ...input },
    });
    return r.invoice;
  },
  sendInvoice: async (id: string, opts?: { processor?: string; test_mode?: boolean }) => {
    return request<{
      ok: true;
      invoice_id: string;
      processor: string;
      pay_url: string;
      portal_return_url: string;
      recipient_email: string;
      test_mode: boolean;
      message_id: string | null;
    }>(`/api/billing/invoices/${id}/send`, {
      method: "POST",
      body: { confirm: true, ...(opts || {}) },
    });
  },
  voidInvoice: async (id: string, reason?: string) => {
    return request<{ ok: true; invoice_id: string; status: "void" }>(`/api/billing/invoices/${id}/void`, {
      method: "POST",
      body: { confirm: true, reason },
    });
  },
  refundInvoice: async (id: string) => {
    return request<{ ok: true; invoice_id: string; status: "refunded"; refund_charge_id: string }>(
      `/api/billing/invoices/${id}/refund`,
      { method: "POST", body: { confirm: true } },
    );
  },
  billingSummary: async (clientId: string) => {
    const r = await request<{ ok: true; client_id: string; summary: BillingSummary }>(
      `/api/clients/${clientId}/billing-summary`,
    );
    return r.summary;
  },
};

/* ---------- company (multi-tenant settings) ---------- */

export interface CompanyProfile {
  name: string;
  slug: string;
  tier: "owner" | "mentorship" | "developer";
  can_whitelabel_software: boolean;
  status: "active" | "suspended";
  subscription_status: "none" | "active" | "past_due" | "canceled";
}

export interface CompanySettings {
  mfsn_affiliate_url?: string | null;
  smartcredit_affiliate_url?: string | null;
  // The company's general sign-up link (docs/INTAKE-LINK-SPEC.md). Read-only URL;
  // the setting round-trips through CompanyUpdate.
  intake_link?: { url: string; enabled: boolean; default_record_type: "lead" | "client" | null } | null;
  business_name?: string | null;
  legal_entity?: string | null;
  logo_url?: string | null;
  from_email?: string | null;
  reply_to_email?: string | null;
  internal_notification_email?: string | null;
  support_email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  comms_provider: "log" | "ghl" | "twilio";
  ghl_location_id?: string | null;
  twilio_from?: string | null;
  ghl_configured?: boolean;
  twilio_configured?: boolean;
  softpull_provider?: string | null;
  softpull_configured?: boolean;
  /** A Discord channel webhook is saved (write-only; never returned). */
  discord_configured?: boolean;
}

export type CompanyUpdate = Partial<Omit<CompanySettings, "ghl_configured" | "twilio_configured" | "softpull_configured" | "discord_configured" | "intake_link">> & {
  intake_link_enabled?: boolean;
  /** Write-only: a Discord webhook URL to save, or "" to remove it. */
  discord_webhook_url?: string;
  // Secrets are write-only — supplied as plaintext, encrypted server-side, never read back.
  ghl_api_key?: string;
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  softpull_api_key?: string;
  softpull_api_secret?: string;
};

// ── Manage staff / admin ──────────────────────────────────────────────────
export interface ScopeDef { scope: string; label: string; note?: string }
export interface ScopeGroup { group: string; scopes: ScopeDef[] }
export interface StaffMember {
  id: string; email: string; name: string | null; role: string;
  created_at: string; last_login_at: string | null; disabled: boolean;
  permissions: string[] | null; allow_all: boolean; effective_scopes: string[];
}
export interface StaffInviteRow {
  id: string; email: string; name: string | null; role: string;
  permissions?: string[] | null; expires_at: string; created_at: string;
}

export const staff = {
  list: () => request<{
    ok: true; staff: StaffMember[]; pending_invites: StaffInviteRow[];
    cap: number; slots_used: number; slots_left: number; permission_catalog: ScopeGroup[];
  }>("/api/staff"),
  invite: (input: { email: string; role: string; name?: string; allow_all?: boolean; permissions?: string[] }) => request<{
    ok: true; invite: StaffInviteRow; invite_url: string; email: { mode: string };
  }>("/api/staff", { method: "POST", body: input }),
  updatePermissions: (id: string, input: { role?: string; allow_all?: boolean; permissions?: string[] }) =>
    request<{ ok: true; id: string; role: string; allow_all: boolean }>(`/api/staff/${id}`, { method: "PATCH", body: input }),
  remove: (id: string, kind: "user" | "invite") =>
    request<{ ok: true }>(`/api/staff/${id}`, { method: "DELETE", body: { kind, confirm: true } }),
};

export const staffInviteApi = {
  validate: (token: string) => request<{
    ok: true; invite: { email: string; name: string | null; role: string; company_name: string; expires_at: string };
  }>(`/api/staff-invite/${token}`, { publicRoute: true }),
  accept: (token: string, password: string, name?: string) => request<{
    ok: true; user: { id: string; email: string; name: string; role: string };
  }>(`/api/staff-invite/${token}/accept`, { method: "POST", body: { password, name }, publicRoute: true }),
};

export const staffAuth = {
  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: true }>("/api/auth/change-password", { method: "POST", body: { current_password, new_password } }),
  forgotPassword: (email: string) =>
    request<{ ok: true; message: string }>("/api/auth/forgot-password", { method: "POST", body: { email }, publicRoute: true }),
  resetPassword: (token: string, password: string) =>
    request<{ ok: true; message: string }>("/api/auth/forgot-password/reset", { method: "POST", body: { token, password }, publicRoute: true }),
};

export const company = {
  get: () => request<{ ok: true; company: CompanyProfile; settings: CompanySettings; intake_link?: CompanySettings["intake_link"] }>("/api/company"),
  update: (patch: CompanyUpdate) => request<{ ok: true }>("/api/company", { method: "PUT", body: patch }),
  uploadLogo: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<{ ok: true; logo_url: string }>("/api/company/logo", { method: "POST", formData: fd });
  },
};

/* ---------- internal notifications (bell) ---------- */

export interface InternalNotification {
  id: string;
  kind: string; // 'birthday' | ...
  client_id?: string | null;
  client_name?: string | null;
  title: string;
  body?: string | null;
  status: "unread" | "read" | "actioned" | "dismissed";
  due_date?: string | null;
  created_at: string;
}

export const notifications = {
  list: () => request<{ ok: true; notifications: InternalNotification[]; unread: number }>("/api/notifications"),
  setStatus: (id: string, status: "read" | "dismissed") =>
    request<{ ok: true }>(`/api/notifications/${id}`, { method: "PATCH", body: { status } }),
  // Birthday action: send a happy-birthday message, then mark the notification actioned.
  send: (id: string, input: { channel: "email" | "sms"; template?: string; custom_message?: string }) =>
    request<{ ok: true; sent: boolean }>(`/api/notifications/${id}/send`, { method: "POST", body: input }),
};

/* ---------- platform admin (super-admin only) ---------- */

export interface AdminCompany {
  id: string;
  name: string;
  slug: string;
  tier: string;
  status: "active" | "suspended";
  access_type: string;
  subscription_status: string;
  client_count: number;
  created_at: string;
}

export interface CompanyInvite {
  id: string;
  token: string;
  tier: "mentorship" | "developer";
  email?: string | null;
  note?: string | null;
  expires_at: string;
  used_at?: string | null;
  invite_url?: string;
  created_at: string;
}

export const admin = {
  listCompanies: () => request<{ ok: true; companies: AdminCompany[] }>("/api/admin/companies"),
  createCompany: (input: { company_name: string; admin_email: string; admin_name: string; admin_password: string; tier?: string }) =>
    request<{ ok: true; company: any; admin: any }>("/api/admin/companies", { method: "POST", body: input }),
  setCompanyStatus: (id: string, status: "active" | "suspended") =>
    request<{ ok: true }>(`/api/admin/companies/${id}`, { method: "PATCH", body: { status } }),
  listInvites: () => request<{ ok: true; invites: CompanyInvite[] }>("/api/admin/invites"),
  createInvite: (input: { tier?: "mentorship" | "developer"; email?: string; note?: string }) =>
    request<{ ok: true; invite: CompanyInvite }>("/api/admin/invites", { method: "POST", body: input }),
};

/* ---------- public invite acceptance ---------- */

export const invite = {
  validate: (token: string) =>
    request<{ ok: true; invite: { tier: string; email?: string | null; note?: string | null } }>(`/api/invite/${token}`, { publicRoute: true }),
  accept: (token: string, input: { company_name: string; admin_name: string; admin_email: string; admin_password: string }) =>
    request<{ ok: true; company: any; admin: any }>(`/api/invite/${token}/accept`, { method: "POST", body: input, publicRoute: true }),
};

export const api = {
  auth,
  dashboard,
  clients,
  intakeInvitations,
  intake,
  reports,
  disputes,
  letters,
  mail,
  filings,
  compliance,
  portal,
  correspondence,
  billing,
  company,
  notifications,
  admin,
  invite,
};

// ── Public sign-up link (/start/:slug) ─────────────────────────────────────
// Contract: docs/INTAKE-LINK-SPEC.md. Public routes: no staff token is sent.
export interface StartPageInfo {
  ok: true;
  slug: string;
  company: { name: string | null; logo_url: string | null; support_email: string | null; phone: string | null };
  monitoring: { my_free_score_now: string | null; smart_credit: string | null };
  default_record_type: "lead" | "client" | null;
}
export interface StartSubmitBody {
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  monitoring_choice: "my_free_score_now" | "smart_credit" | null;
  consent: true;
  source: string | null;
  referrer: string | null;
  /** honeypot — must stay empty */
  website: string;
}
export interface StartSubmitResult {
  ok: true;
  intake_url: string | null;
  monitoring_url: string | null;
  client_state: "created" | "existing";
  /** existing clients only: was the secure link actually emailed to the address on file? */
  delivery?: "sent" | "failed" | null;
}
export const startApi = {
  get: (slug: string) => request<StartPageInfo>(`/api/start/${encodeURIComponent(slug)}`, { publicRoute: true }),
  submit: (slug: string, body: StartSubmitBody) =>
    request<StartSubmitResult>(`/api/start/${encodeURIComponent(slug)}`, { method: "POST", body, publicRoute: true }),
};
